import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  AlertCircle,
  CalendarDays,
  ChevronLeft,
  Eye,
  Image as ImageIcon,
  ListPlus,
  Minus,
  Pill,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Trash2,
  Upload,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { consultationApi } from '@/services/clinicApi';
import { resolveBackendUrl } from '@/config/apiConfig';
import { medicalService } from '@/services/api';

const SUPPORTED_IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.webp', '.gif']);

const readMap = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const readList = (value) => (Array.isArray(value) ? value : []);

const readString = (value) => {
  if (value == null || value === false) return '';
  if (typeof value === 'object' && !Array.isArray(value)) {
    for (const key of ['name', 'display_name', 'value', 'label', 'ar', 'en', 'note', 'notes']) {
      const resolved = readString(value[key]);
      if (resolved) return resolved;
    }
    return '';
  }

  const normalized = String(value).trim();
  return normalized || '';
};

const readNumber = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const stripHtml = (value) => readString(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const extractBase64Payload = (value) => {
  const normalized = readString(value).trim();
  if (!normalized) return '';
  if (!normalized.startsWith('data:')) return normalized;

  const commaIndex = normalized.indexOf(',');
  if (commaIndex === -1) return '';
  return normalized.slice(commaIndex + 1).trim();
};

const hasUsableInlineImage = (value) => {
  const payload = extractBase64Payload(value);
  if (!payload) return false;
  if (payload === 'False' || payload === 'false' || payload === 'null') return false;
  return payload.length > 24;
};

const parseDateValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toApiDateTime = (value) => {
  if (!value) return null;
  return format(value, 'yyyy-MM-dd HH:mm:ss');
};

const buildMedicineExternalId = (value) =>
  `local_${value.trim().toLowerCase().replace(/\s+/g, '_')}`;

const normalizeOptionNames = (values) => {
  const names = [];
  values.forEach((value) => {
    const normalized = readString(value).trim();
    if (normalized && !names.includes(normalized)) {
      names.push(normalized);
    }
  });
  return names;
};

const extractOptionNames = (raw) => {
  const payload = readMap(raw);
  const wrappedData = payload.data;
  if (Array.isArray(wrappedData)) {
    return normalizeOptionNames(wrappedData.map((item) => readString(readMap(item).name)));
  }

  return normalizeOptionNames(
    readList(raw).map((item) => readString(readMap(item).name))
  );
};

const buildMedicines = (payload) => {
  const medicines = readList(payload.medicines)
    .map((item) => {
      const row = readMap(item);
      const name = stripHtml(row.name || row.display_name || row.label || item);
      return {
        name,
        externalId: readString(row.external_id || row.id) || buildMedicineExternalId(name),
      };
    })
    .filter((item) => item.name);

  if (medicines.length > 0) {
    return medicines;
  }

  return readList(payload.permanent_medications || payload.permanent_medication)
    .map((item) => {
      const row = readMap(item);
      const name = stripHtml(row.name || row.display_name || row.label || item);
      return {
        name,
        externalId: readString(row.external_id || row.id) || buildMedicineExternalId(name),
      };
    })
    .filter((item) => item.name);
};

const guessImageMimeType = (fileName = '') => {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.gif')) return 'image/gif';
  return 'image/jpeg';
};

const imageExtension = (fileName = '') => {
  const normalized = fileName.trim().toLowerCase();
  const dotIndex = normalized.lastIndexOf('.');
  if (dotIndex === -1 || dotIndex === normalized.length - 1) return null;
  return normalized.slice(dotIndex);
};

const isSupportedImageFile = (fileName) => {
  const extension = imageExtension(fileName);
  return extension ? SUPPORTED_IMAGE_EXTENSIONS.has(extension) : false;
};

const supportedImageFormatsLabel = () =>
  Array.from(SUPPORTED_IMAGE_EXTENSIONS)
    .map((value) => value.replace('.', '').toUpperCase())
    .join(', ');

const appendApiToken = (url, token) => {
  const normalizedUrl = readString(url).trim();
  const normalizedToken = readString(token).trim();
  if (!normalizedUrl) return '';

  const resolvedUrl = resolveBackendUrl(normalizedUrl);
  if (!normalizedToken) return resolvedUrl;
  if (resolvedUrl.includes('api_token=')) return resolvedUrl;

  const separator = resolvedUrl.includes('?') ? '&' : '?';
  return `${resolvedUrl}${separator}api_token=${encodeURIComponent(normalizedToken)}`;
};

const buildImageSourceCandidates = ({ imageData, imageName, token }) => {
  const candidates = [];
  const rawData = readString(imageData.data || imageData.base64).trim();

  if (hasUsableInlineImage(rawData)) {
    candidates.push(
      rawData.startsWith('data:')
        ? rawData
        : `data:${guessImageMimeType(imageName)};base64,${rawData}`
    );
  }

  [
    imageData.secure_preview_url,
    imageData.preview_url,
    imageData.download_url,
    imageData.url,
    imageData.original_url,
  ].forEach((value) => {
    const resolved = appendApiToken(value, token);
    if (resolved) {
      candidates.push(resolved);
    }
  });

  return [...new Set(candidates.filter(Boolean))];
};

const ConsultationImageCard = ({ image, onOpen, onRemove, onError }) => (
  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
    <button type="button" className="block w-full" onClick={() => onOpen(image)}>
      <img
        src={image.previewUrl}
        alt={image.name}
        className="h-40 w-full object-cover"
        onError={() => onError(image)}
      />
    </button>
    <div className="flex items-center justify-between gap-3 p-3">
      <span className="truncate text-sm text-slate-700 dark:text-slate-200">{image.name}</span>
      <div className="flex items-center gap-2">
        <Button type="button" size="icon" variant="ghost" onClick={() => onOpen(image)}>
          <Eye className="h-4 w-4" />
        </Button>
        <Button type="button" size="icon" variant="ghost" onClick={() => onRemove(image)}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  </div>
);

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(readString(reader.result));
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const DoctorConsultationEditPage = () => {
  const { consultationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;
  const fileInputRef = useRef(null);
  const imageViewportRef = useRef(null);
  const isDraggingImageRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  const [patientId, setPatientId] = useState(readNumber(location.state?.patientId));
  const [appointmentId, setAppointmentId] = useState(null);
  const [consultationDate, setConsultationDate] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [diagnosis, setDiagnosis] = useState('');
  const [notes, setNotes] = useState('');
  const [medicines, setMedicines] = useState([]);
  const [medicineInput, setMedicineInput] = useState('');
  const [savedMedicineSuggestions, setSavedMedicineSuggestions] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [viewerScale, setViewerScale] = useState(1);
  const [viewerOffset, setViewerOffset] = useState({ x: 0, y: 0 });

  const resetViewerTransform = useCallback(() => {
    setViewerScale(1);
    setViewerOffset({ x: 0, y: 0 });
    isDraggingImageRef.current = false;
    dragStartRef.current = { x: 0, y: 0 };
  }, []);

  useEffect(() => {
    resetViewerTransform();
  }, [viewerImage, resetViewerTransform]);

  const updateViewerScale = useCallback((nextScale) => {
    const clampedScale = Math.max(1, Math.min(4, nextScale));
    setViewerScale(clampedScale);
    if (clampedScale === 1) {
      setViewerOffset({ x: 0, y: 0 });
    }
  }, []);

  const handleViewerWheel = useCallback((event) => {
    event.preventDefault();
    const delta = event.deltaY < 0 ? 0.25 : -0.25;
    updateViewerScale(viewerScale + delta);
  }, [updateViewerScale, viewerScale]);

  const handleViewerMouseDown = useCallback((event) => {
    if (viewerScale <= 1) return;
    isDraggingImageRef.current = true;
    dragStartRef.current = {
      x: event.clientX - viewerOffset.x,
      y: event.clientY - viewerOffset.y,
    };
  }, [viewerOffset.x, viewerOffset.y, viewerScale]);

  const handleViewerMouseMove = useCallback((event) => {
    if (!isDraggingImageRef.current || viewerScale <= 1) return;
    setViewerOffset({
      x: event.clientX - dragStartRef.current.x,
      y: event.clientY - dragStartRef.current.y,
    });
  }, [viewerScale]);

  const stopViewerDragging = useCallback(() => {
    isDraggingImageRef.current = false;
  }, []);

  const loadSavedMedicineSuggestions = useCallback(async () => {
    try {
      const response = await medicalService.getHistoryOptions();
      const payload = readMap(response?.data);
      const data = readMap(payload.data);
      return extractOptionNames(data.permanent_medications);
    } catch (_) {
      try {
        const response = await medicalService.getMedications();
        return extractOptionNames(response?.data?.data || response?.data);
      } catch (_) {
        return [];
      }
    }
  }, []);

  const resolveConsultationImages = useCallback(async (data) => {
    const token = localStorage.getItem('token') || '';
    const resolved = [];

    const resolveImageEntries = async (entries) => {
      for (const item of entries) {
        const imageData = readMap(item);
        const attachmentId = readNumber(imageData.id || imageData.attachment_id);
        const imageName = readString(imageData.name || imageData.file_name || 'image').trim() || 'image';
        const sources = buildImageSourceCandidates({ imageData, imageName, token });
        const previewUrl = sources[0] || '';

        if (previewUrl) {
          resolved.push({
            attachmentId,
            name: imageName,
            previewUrl,
            sources,
            sourceIndex: 0,
          });
        }
      }
    };

    await resolveImageEntries(readList(data.images));
    if (resolved.length > 0) return resolved;

    const directImage = readString(data.image).trim();
    if (directImage) {
      const resolvedUrl = appendApiToken(directImage, token) || resolveBackendUrl(directImage);
      if (resolvedUrl) {
        return [
          {
            attachmentId: readNumber((/\/web\/content\/(\d+)/.exec(directImage) || [])[1]),
            name: directImage.split('/').pop() || 'image',
            previewUrl: resolvedUrl,
            sources: [resolvedUrl],
            sourceIndex: 0,
          },
        ];
      }
    }

    await resolveImageEntries(
      readList(data.media).filter((item) => {
        const collectionName = readString(readMap(item).collection_name);
        return !collectionName || collectionName === 'image' || collectionName === 'images';
      })
    );

    return resolved;
  }, []);

  const fetchConsultation = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [consultationResponse, suggestions] = await Promise.all([
        consultationApi.getById(consultationId),
        loadSavedMedicineSuggestions(),
      ]);
      const payload = readMap(consultationResponse?.data?.data);
      const data = Object.keys(payload).length > 0 ? payload : readMap(consultationResponse?.data);
      const resolvedPatientId = readNumber(data.patient_id) || readNumber(location.state?.patientId);
      const resolvedConsultationDate =
        parseDateValue(data.date || data.created_at) || new Date();

      setPatientId(resolvedPatientId);
      setAppointmentId(readNumber(data.appointment_id));
      setConsultationDate(resolvedConsultationDate);
      setUpdatedAt(parseDateValue(data.updated_at || data.date));
      setDiagnosis(stripHtml(data.diagnosis));
      setNotes(stripHtml(data.note || data.notes));
      setMedicines(buildMedicines(data));
      setSavedMedicineSuggestions(suggestions);
      setSelectedImages([]);

      const resolvedImages = await resolveConsultationImages(data);
      setExistingImages(resolvedImages);
    } catch (fetchError) {
      console.error('Failed to load consultation:', fetchError);
      setError(lang === 'ar' ? 'فشل في تحميل بيانات المعاينة' : 'Failed to load consultation');
    } finally {
      setLoading(false);
    }
  }, [consultationId, lang, loadSavedMedicineSuggestions, location.state?.patientId, resolveConsultationImages]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    void fetchConsultation();
  }, [fetchConsultation, isAuthenticated, navigate]);

  const containsMedicine = useCallback(
    (value) => {
      const normalized = value.trim().toLowerCase();
      return medicines.some((item) => item.name.trim().toLowerCase() === normalized);
    },
    [medicines]
  );

  const availableSuggestions = useMemo(
    () =>
      savedMedicineSuggestions.filter(
        (item) => item.trim() && !medicines.some((medicine) => medicine.name.trim().toLowerCase() === item.trim().toLowerCase())
      ),
    [medicines, savedMedicineSuggestions]
  );

  const addMedicine = useCallback(() => {
    const value = medicineInput.trim();
    if (!value || containsMedicine(value)) {
      setMedicineInput('');
      return;
    }

    setMedicines((current) => [
      ...current,
      {
        name: value,
        externalId: buildMedicineExternalId(value),
      },
    ]);
    setMedicineInput('');
  }, [containsMedicine, medicineInput]);

  const selectSuggestedMedicine = useCallback(
    (value) => {
      const normalized = value.trim();
      if (!normalized || containsMedicine(normalized)) return;

      setMedicines((current) => [
        ...current,
        {
          name: normalized,
          externalId: buildMedicineExternalId(normalized),
        },
      ]);
      setPickerOpen(false);
    },
    [containsMedicine]
  );

  const removeMedicine = (name) => {
    setMedicines((current) => current.filter((item) => item.name !== name));
  };

  const removeExistingImage = (target) => {
    setExistingImages((current) => current.filter((item) => item !== target));
  };

  const advanceExistingImageSource = useCallback((target) => {
    setExistingImages((current) =>
      current.map((item) => {
        if (item !== target) return item;
        const nextIndex = (item.sourceIndex ?? 0) + 1;
        const nextSource = item.sources?.[nextIndex] || '';
        return {
          ...item,
          sourceIndex: nextIndex,
          previewUrl: nextSource,
        };
      })
    );

    setViewerImage((current) => {
      if (current !== target) return current;
      const nextIndex = (current.sourceIndex ?? 0) + 1;
      return {
        ...current,
        sourceIndex: nextIndex,
        previewUrl: current.sources?.[nextIndex] || '',
      };
    });
  }, []);

  const removeSelectedImage = (target) => {
    setSelectedImages((current) => current.filter((item) => item !== target));
  };

  const advanceSelectedImageSource = useCallback((target) => {
    setSelectedImages((current) =>
      current.map((item) => {
        if (item !== target) return item;
        const nextIndex = (item.sourceIndex ?? 0) + 1;
        return {
          ...item,
          sourceIndex: nextIndex,
          previewUrl: item.sources?.[nextIndex] || '',
        };
      })
    );

    setViewerImage((current) => {
      if (current !== target) return current;
      const nextIndex = (current.sourceIndex ?? 0) + 1;
      return {
        ...current,
        sourceIndex: nextIndex,
        previewUrl: current.sources?.[nextIndex] || '',
      };
    });
  }, []);

  const handlePickImages = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const nextImages = [];
    const unsupportedFileNames = [];

    for (const file of files) {
      if (!isSupportedImageFile(file.name)) {
        unsupportedFileNames.push(file.name);
        continue;
      }

      try {
        const dataUrl = await fileToDataUrl(file);
        if (!dataUrl) continue;

        nextImages.push({
          file,
          name: file.name,
          previewUrl: dataUrl,
          mimeType: file.type || guessImageMimeType(file.name),
          dataUrl,
          sources: [dataUrl],
          sourceIndex: 0,
        });
      } catch (_) {
        continue;
      }
    }

    if (!nextImages.length) {
      toast.error(
        lang === 'ar'
          ? `لم يتم تحميل أي صورة. الصيغ المدعومة: ${supportedImageFormatsLabel()}`
          : `No images were loaded. Supported formats: ${supportedImageFormatsLabel()}`
      );
      event.target.value = '';
      return;
    }

    setSelectedImages((current) => [...current, ...nextImages]);

    if (unsupportedFileNames.length > 0) {
      toast.error(
        lang === 'ar'
          ? `تم تجاهل بعض الصور لأن صيغتها غير مدعومة: ${unsupportedFileNames.join(', ')}`
          : `Some images were skipped because their format is not supported: ${unsupportedFileNames.join(', ')}`
      );
    }

    event.target.value = '';
  };

  const handleSave = async () => {
    if (!diagnosis.trim()) {
      toast.error(lang === 'ar' ? 'التشخيص مطلوب' : 'Diagnosis is required');
      return;
    }

    setSaving(true);

    try {
      const formattedDate = toApiDateTime(consultationDate);
      const medicinesPayload = medicines.map((item) => ({
        name: item.name,
        external_id: item.externalId,
      }));
      const permanentMedicationsPayload = medicines.map((item) => ({ name: item.name }));
      const imagesPayload = [
        ...existingImages
          .filter((item) => item.attachmentId != null && item.attachmentId > 0)
          .map((item) => ({ id: item.attachmentId })),
        ...selectedImages.map((item) => ({
          name: item.name,
          mimetype: item.mimeType || guessImageMimeType(item.name),
          data: item.dataUrl,
        })),
      ];

      await consultationApi.update(consultationId, {
        ...(formattedDate ? { consultation_date: formattedDate, date: formattedDate } : {}),
        diagnosis: diagnosis.trim(),
        note: notes.trim(),
        notes: notes.trim(),
        ...(appointmentId ? { appointment_id: appointmentId } : {}),
        ...(patientId ? { patient_id: patientId } : {}),
        medicines: medicinesPayload,
        permanent_medication: permanentMedicationsPayload,
        permanent_medications: permanentMedicationsPayload,
        images: imagesPayload,
      });

      toast.success(lang === 'ar' ? 'تم تحديث المعاينة بنجاح' : 'Consultation updated successfully');

      if (patientId) {
        navigate(`/doctor/patients/${patientId}`, {
          replace: true,
          state: { refreshAt: Date.now() },
        });
        return;
      }

      navigate(-1);
    } catch (saveError) {
      console.error('Failed to update consultation:', saveError);
      toast.error(lang === 'ar' ? 'فشل في تحديث المعاينة' : 'Failed to update consultation');
    } finally {
      setSaving(false);
    }
  };

  const updatedAtLabel =
    updatedAt || consultationDate
      ? `${lang === 'ar' ? 'آخر تحديث' : 'Last update'}: ${format(updatedAt || consultationDate, 'yyyy-MM-dd hh:mm a')}`
      : lang === 'ar'
        ? 'آخر تحديث غير متاح'
        : 'Last update unavailable';

  if (!isAuthenticated) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
        <div className="mx-auto max-w-4xl space-y-6">
          <Skeleton className="h-12 w-32 rounded-xl" />
          <Skeleton className="h-40 rounded-3xl" />
          <Skeleton className="h-96 rounded-3xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="mx-auto max-w-4xl px-4 py-6">
        <div className="mb-6 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            className="gap-2"
            onClick={() => (patientId ? navigate(`/doctor/patients/${patientId}`) : navigate(-1))}
          >
            <ChevronLeft className="h-4 w-4" />
            {lang === 'ar' ? 'العودة' : 'Back'}
          </Button>

          <Button variant="outline" size="icon" onClick={fetchConsultation} disabled={loading || saving}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <Card className="mb-6 rounded-[28px] border-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 text-white shadow-xl shadow-slate-900/20">
          <CardContent className="flex flex-col gap-4 p-6 md:flex-row md:items-end md:justify-between">
            <div className="space-y-3">
              <h1 className="text-3xl font-bold">{lang === 'ar' ? 'تعديل' : 'Edit'}</h1>
              <p className="text-lg font-medium text-indigo-200">{updatedAtLabel}</p>
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-xl bg-white text-slate-900 hover:bg-slate-100"
            >
              <Save className="me-2 h-4 w-4" />
              {saving ? (lang === 'ar' ? 'جارٍ الحفظ...' : 'Saving...') : (lang === 'ar' ? 'حفظ التعديلات' : 'Save changes')}
            </Button>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {error && (
            <Card className="rounded-3xl border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/20">
              <CardContent className="flex items-center gap-3 p-5 text-red-700 dark:text-red-300">
                <AlertCircle className="h-5 w-5" />
                <p>{error}</p>
              </CardContent>
            </Card>
          )}

          <Card className="rounded-3xl border-0 shadow-sm dark:bg-slate-800">
            <CardHeader>
              <CardTitle className="text-indigo-600 dark:text-indigo-300">
                {lang === 'ar' ? 'تاريخ المعاينة' : 'Consultation Date'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-5 text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white">
                <CalendarDays className="h-7 w-7 text-slate-500" />
                <div className="text-lg font-bold">
                  {consultationDate ? format(consultationDate, 'yyyy-MM-dd') : '--'}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-0 shadow-sm dark:bg-slate-800">
            <CardHeader>
              <CardTitle className="text-indigo-600 dark:text-indigo-300">
                {lang === 'ar' ? 'التشخيص' : 'Diagnosis'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="diagnosis">{lang === 'ar' ? 'التشخيص' : 'Diagnosis'}</Label>
                <Input
                  id="diagnosis"
                  value={diagnosis}
                  onChange={(event) => setDiagnosis(event.target.value)}
                  placeholder={lang === 'ar' ? 'أدخل التشخيص' : 'Enter diagnosis'}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-0 shadow-sm dark:bg-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-indigo-600 dark:text-indigo-300">
                <Pill className="h-5 w-5" />
                {lang === 'ar' ? 'الأدوية' : 'Medicines'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {medicines.length === 0 ? (
                <p className="text-right text-base text-slate-400">
                  {lang === 'ar' ? 'لا توجد بيانات' : 'No data found'}
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {medicines.map((item) => (
                    <Badge
                      key={`${item.externalId}-${item.name}`}
                      variant="secondary"
                      className="flex items-center gap-2 rounded-full bg-indigo-100 px-3 py-2 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-200"
                    >
                      <span>{item.name}</span>
                      <button
                        type="button"
                        onClick={() => removeMedicine(item.name)}
                        className="rounded-full text-indigo-700 transition hover:text-red-500 dark:text-indigo-200 dark:hover:text-red-300"
                        aria-label={lang === 'ar' ? `حذف ${item.name}` : `Remove ${item.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              <div className="flex flex-col gap-3 md:flex-row">
                <Button type="button" onClick={addMedicine} className="h-12 min-w-[160px] gap-2 bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="h-4 w-4" />
                  {lang === 'ar' ? 'إضافة' : 'Add'}
                </Button>
                <Input
                  value={medicineInput}
                  onChange={(event) => setMedicineInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addMedicine();
                    }
                  }}
                  placeholder={lang === 'ar' ? 'اسم الدواء' : 'Medicine name'}
                  className="h-12"
                />
              </div>

              <Button
                type="button"
                variant="outline"
                className="h-12 w-full gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-400/40 dark:text-indigo-200 dark:hover:bg-indigo-500/10"
                onClick={() => {
                  if (!availableSuggestions.length) {
                    toast.error(lang === 'ar' ? 'لا توجد خيارات محفوظة' : 'No saved options found');
                    return;
                  }
                  setPickerOpen(true);
                }}
              >
                <ListPlus className="h-4 w-4" />
                {lang === 'ar' ? 'اختيار من القائمة الجاهزة' : 'Choose from saved list'}
              </Button>
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-0 shadow-sm dark:bg-slate-800">
            <CardHeader>
              <CardTitle className="text-indigo-600 dark:text-indigo-300">
                {lang === 'ar' ? 'تفاصيل إضافية' : 'Additional details'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                id="notes"
                rows={6}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder={lang === 'ar' ? 'اكتب الملاحظات هنا' : 'Write notes here'}
              />
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-0 shadow-sm dark:bg-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-indigo-600 dark:text-indigo-300">
                <ImageIcon className="h-5 w-5" />
                {lang === 'ar' ? 'صورة' : 'Image'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                >
                  <Upload className="h-4 w-4" />
                  {lang === 'ar' ? 'اختر' : 'Choose'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.gif"
                  multiple
                  className="hidden"
                  onChange={handlePickImages}
                />
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {existingImages.length === 0 && selectedImages.length === 0
                    ? (lang === 'ar' ? 'لم يتم اختيار صورة' : 'No image selected')
                    : `${lang === 'ar' ? 'الصور' : 'Images'}: ${existingImages.length + selectedImages.length}`}
                </p>
              </div>

              {existingImages.length > 0 || selectedImages.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  {existingImages.map((image) => (
                    <ConsultationImageCard
                      key={`existing-${image.attachmentId || image.name}`}
                      image={image}
                      onOpen={setViewerImage}
                      onRemove={removeExistingImage}
                      onError={advanceExistingImageSource}
                    />
                  ))}

                  {selectedImages.map((image) => (
                    <ConsultationImageCard
                      key={`selected-${image.name}-${image.previewUrl.slice(0, 24)}`}
                      image={image}
                      onOpen={setViewerImage}
                      onRemove={removeSelectedImage}
                      onError={advanceSelectedImageSource}
                    />
                  ))}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{lang === 'ar' ? 'الأدوية' : 'Medicines'}</DialogTitle>
            <DialogDescription>
              {lang === 'ar' ? 'اختر من الأدوية المحفوظة مسبقًا' : 'Choose from saved medicines'}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {availableSuggestions.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => selectSuggestedMedicine(item)}
                className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-4 py-3 text-left transition hover:border-indigo-300 hover:bg-indigo-50 dark:border-slate-700 dark:hover:bg-slate-800"
              >
                <span>{item}</span>
                <Plus className="h-4 w-4 text-indigo-500" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewerImage} onOpenChange={(open) => {
        if (!open) {
          setViewerImage(null);
          resetViewerTransform();
        }
      }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{viewerImage?.name || (lang === 'ar' ? 'صورة' : 'Image')}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center gap-2">
            <Button type="button" size="icon" variant="outline" onClick={() => updateViewerScale(viewerScale - 0.25)}>
              <Minus className="h-4 w-4" />
            </Button>
            <div className="min-w-16 text-center text-sm font-medium text-slate-600 dark:text-slate-300">
              {Math.round(viewerScale * 100)}%
            </div>
            <Button type="button" size="icon" variant="outline" onClick={() => updateViewerScale(viewerScale + 0.25)}>
              <Plus className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" className="gap-2" onClick={resetViewerTransform}>
              <RotateCcw className="h-4 w-4" />
              {lang === 'ar' ? 'إعادة ضبط' : 'Reset'}
            </Button>
          </div>
          {viewerImage ? (
            <div
              ref={imageViewportRef}
              className="max-h-[75vh] w-full overflow-hidden rounded-2xl bg-slate-100 dark:bg-slate-900"
              onWheel={handleViewerWheel}
              onMouseMove={handleViewerMouseMove}
              onMouseUp={stopViewerDragging}
              onMouseLeave={stopViewerDragging}
            >
              <img
                src={viewerImage.previewUrl}
                alt={viewerImage.name}
                className="max-h-[75vh] w-full select-none object-contain"
                style={{
                  transform: `translate(${viewerOffset.x}px, ${viewerOffset.y}px) scale(${viewerScale})`,
                  transformOrigin: 'center center',
                  cursor: viewerScale > 1 ? (isDraggingImageRef.current ? 'grabbing' : 'grab') : 'default',
                  transition: isDraggingImageRef.current ? 'none' : 'transform 120ms ease-out',
                }}
                draggable={false}
                onMouseDown={handleViewerMouseDown}
                onError={() => {
                  const nextIndex = (viewerImage.sourceIndex ?? 0) + 1;
                  const nextSource = viewerImage.sources?.[nextIndex] || '';
                  if (!nextSource) return;
                  setViewerImage({
                    ...viewerImage,
                    sourceIndex: nextIndex,
                    previewUrl: nextSource,
                  });
                }}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorConsultationEditPage;
